import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  CardActions,
  Collapse,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  LocalShipping as BostaIcon,
  LocationOn as LocationIcon,
  AttachMoney as PriceIcon,
  Speed as SpeedIcon,
  Public as ApiIcon,
  Store as StoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Map as MapIcon
} from '@mui/icons-material';
import {
  ShippingSettings as ShippingSettingsType,
  ShippingMethod,
  ShippingRegion,
  ShippingCity,
  BostaSettings,
  PickupLocation
} from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import { getBostaCities } from '../../../services/bostaService';
import { BostaCity } from '../../../types/bosta';
import { DEFAULT_EGYPT_SHIPPING_REGIONS } from '../../../services/storeSettingsService';

// ====================================================
// تحويل مدن Bosta إلى مناطق شحن قابلة للتعديل
// ====================================================

// أسعار افتراضية حسب المدينة (قابلة للتعديل)
const BOSTA_DEFAULT_PRICES: Record<string, { cost: number; days: number }> = {
  'القاهرة': { cost: 40, days: 2 },
  'الجيزة': { cost: 45, days: 2 },
  'القليوبية': { cost: 50, days: 3 },
  'الإسكندرية': { cost: 60, days: 3 },
  'الدقهلية': { cost: 70, days: 4 },
  'الشرقية': { cost: 65, days: 4 },
  'الغربية': { cost: 65, days: 4 },
  'المنوفية': { cost: 70, days: 4 },
  'البحيرة': { cost: 70, days: 4 },
  'كفر الشيخ': { cost: 75, days: 4 },
  'دمياط': { cost: 75, days: 4 },
  'بورسعيد': { cost: 75, days: 4 },
  'الإسماعيلية': { cost: 70, days: 4 },
  'السويس': { cost: 75, days: 4 },
  'أسيوط': { cost: 80, days: 5 },
  'سوهاج': { cost: 85, days: 5 },
  'قنا': { cost: 85, days: 5 },
  'الأقصر': { cost: 90, days: 6 },
  'أسوان': { cost: 90, days: 6 },
  'المنيا': { cost: 80, days: 5 },
  'بني سويف': { cost: 75, days: 5 },
  'الفيوم': { cost: 70, days: 4 },
  'شمال سيناء': { cost: 90, days: 6 },
  'جنوب سيناء': { cost: 95, days: 7 },
  'البحر الأحمر': { cost: 95, days: 7 },
  'الوادي الجديد': { cost: 100, days: 7 },
  'مطروح': { cost: 95, days: 7 },
};

// تحديد المنطقة الجغرافية لكل مدينة
const CITY_REGION_MAP: Record<string, string> = {
  'القاهرة': 'القاهرة الكبرى',
  'الجيزة': 'القاهرة الكبرى',
  'القليوبية': 'القاهرة الكبرى',
  'الإسكندرية': 'الوجه البحري',
  'الدقهلية': 'الوجه البحري',
  'الشرقية': 'الوجه البحري',
  'الغربية': 'الوجه البحري',
  'المنوفية': 'الوجه البحري',
  'البحيرة': 'الوجه البحري',
  'كفر الشيخ': 'الوجه البحري',
  'دمياط': 'الوجه البحري',
  'بورسعيد': 'قناة السويس',
  'الإسماعيلية': 'قناة السويس',
  'السويس': 'قناة السويس',
  'أسيوط': 'الصعيد',
  'سوهاج': 'الصعيد',
  'قنا': 'الصعيد',
  'الأقصر': 'الصعيد',
  'أسوان': 'الصعيد',
  'المنيا': 'مصر الوسطى',
  'بني سويف': 'مصر الوسطى',
  'الفيوم': 'مصر الوسطى',
  'شمال سيناء': 'سيناء',
  'جنوب سيناء': 'سيناء',
  'البحر الأحمر': 'المناطق النائية',
  'الوادي الجديد': 'المناطق النائية',
  'مطروح': 'المناطق النائية',
};

const convertBostaCitiesToShippingRegions = (bostaCities: BostaCity[]): ShippingRegion[] => {
  return bostaCities
    .map((bostaCity, index) => {
      const cityName = bostaCity.name || '';
      const defaultPricing = BOSTA_DEFAULT_PRICES[cityName] || { cost: 60, days: 3 };

      return {
        id: `bosta-region-${bostaCity._id || index}-${Date.now()}`,
        name: cityName,
        isActive: bostaCity.active !== false,
        cities: [
          {
            id: `bosta-city-${bostaCity._id || index}`,
            name: cityName,
            shippingCost: defaultPricing.cost,
            estimatedDeliveryDays: defaultPricing.days,
            isActive: bostaCity.active !== false
          }
        ]
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'ar'));
};

interface ShippingSettingsProps {
  settings: ShippingSettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

// مكون عرض منطقة الشحن في وضع القراءة
const RegionDisplayCard: React.FC<{ region: ShippingRegion }> = ({ region }) => {
  const [expanded, setExpanded] = useState(false);
  const activeCities = region.cities.filter(c => c.isActive);
  const minPrice = activeCities.length > 0 ? Math.min(...activeCities.map(c => c.shippingCost)) : 0;
  const maxPrice = activeCities.length > 0 ? Math.max(...activeCities.map(c => c.shippingCost)) : 0;

  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 1.5, cursor: 'pointer',
          bgcolor: region.isActive ? 'transparent' : 'action.disabledBackground'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MapIcon sx={{ color: region.isActive ? 'primary.main' : 'text.disabled', fontSize: 18 }} />
          <Typography variant="body2" fontWeight={600}>{region.name}</Typography>
          {!region.isActive && <Chip label="معطل" size="small" color="default" />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${activeCities.length} مدينة`}
            size="small"
            color={activeCities.length > 0 ? 'primary' : 'default'}
            variant="outlined"
          />
          {activeCities.length > 0 && (
            <Chip
              label={minPrice === maxPrice ? `${minPrice} ج.م` : `${minPrice} - ${maxPrice} ج.م`}
              size="small"
              color="success"
              variant="outlined"
              icon={<PriceIcon />}
            />
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          {region.cities.length === 0 ? (
            <Typography variant="caption" color="text.secondary">لا توجد مدن مضافة</Typography>
          ) : (
            <Grid container spacing={0.5}>
              {region.cities.map(city => (
                <Grid item key={city.id}>
                  <Tooltip title={`${city.estimatedDeliveryDays} أيام`} arrow>
                    <Chip
                      label={`${city.name} - ${city.shippingCost} ج.م`}
                      size="small"
                      color={city.isActive ? 'default' : 'default'}
                      sx={{
                        opacity: city.isActive ? 1 : 0.5,
                        textDecoration: city.isActive ? 'none' : 'line-through'
                      }}
                    />
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// مكون تحرير منطقة الشحن
const RegionEditCard: React.FC<{
  region: ShippingRegion;
  regionIndex: number;
  onUpdate: (regionIndex: number, updatedRegion: ShippingRegion) => void;
  onDelete: (regionIndex: number) => void;
}> = ({ region, regionIndex, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const updateCity = (cityIndex: number, updatedCity: ShippingCity) => {
    const updatedCities = [...region.cities];
    updatedCities[cityIndex] = updatedCity;
    onUpdate(regionIndex, { ...region, cities: updatedCities });
  };

  const deleteCity = (cityIndex: number) => {
    const updatedCities = region.cities.filter((_, i) => i !== cityIndex);
    onUpdate(regionIndex, { ...region, cities: updatedCities });
  };

  const addCity = () => {
    const newCity: ShippingCity = {
      id: `city-${Date.now()}`,
      name: 'مدينة جديدة',
      shippingCost: 50,
      estimatedDeliveryDays: 3,
      isActive: true
    };
    onUpdate(regionIndex, { ...region, cities: [...region.cities, newCity] });
  };

  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
        <TextField
          label="اسم المنطقة"
          value={region.name}
          onChange={(e) => onUpdate(regionIndex, { ...region, name: e.target.value })}
          size="small"
          sx={{ flexGrow: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={region.isActive}
              onChange={(e) => onUpdate(regionIndex, { ...region, isActive: e.target.checked })}
              size="small"
            />
          }
          label="مفعل"
          sx={{ mr: 0 }}
        />
        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
          color="primary"
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <IconButton color="error" size="small" onClick={() => onDelete(regionIndex)}>
          <DeleteIcon />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">المدن في هذه المنطقة</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addCity}>
              إضافة مدينة
            </Button>
          </Box>

          {region.cities.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 1 }}>
              لا توجد مدن - اضغط "إضافة مدينة"
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>المدينة</TableCell>
                    <TableCell align="center">التكلفة (ج.م)</TableCell>
                    <TableCell align="center">الأيام</TableCell>
                    <TableCell align="center">مفعل</TableCell>
                    <TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {region.cities.map((city, cityIndex) => (
                    <TableRow key={city.id}>
                      <TableCell>
                        <TextField
                          value={city.name}
                          onChange={(e) => updateCity(cityIndex, { ...city, name: e.target.value })}
                          size="small"
                          variant="standard"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          value={city.shippingCost}
                          onChange={(e) => updateCity(cityIndex, { ...city, shippingCost: Number(e.target.value) })}
                          type="number"
                          size="small"
                          variant="standard"
                          inputProps={{ min: 0, style: { textAlign: 'center' } }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          value={city.estimatedDeliveryDays}
                          onChange={(e) => updateCity(cityIndex, { ...city, estimatedDeliveryDays: Number(e.target.value) })}
                          type="number"
                          size="small"
                          variant="standard"
                          inputProps={{ min: 1, style: { textAlign: 'center' } }}
                          sx={{ width: 60 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={city.isActive}
                          onChange={(e) => updateCity(cityIndex, { ...city, isActive: e.target.checked })}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => deleteCity(cityIndex)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// مكون عرض شركة الشحن (وضع القراءة)
const ShippingMethodCard: React.FC<{
  method: ShippingMethod;
  onEdit: () => void;
  onDelete: () => void;
  editing: boolean;
}> = ({ method, onEdit, onDelete, editing }) => {
  const regions = method.regions || [];
  const allCities = regions.flatMap(r => r.cities.filter(c => c.isActive));
  const totalCities = allCities.length;
  const activeRegions = regions.filter(r => r.isActive).length;

  const getTypeLabel = () => {
    switch (method.shippingType) {
      case 'pickup': return { label: 'استلام من المتجر', color: 'info' as const, icon: <StoreIcon fontSize="small" /> };
      case 'api': return { label: `API - ${method.apiProvider || 'Bosta'}`, color: 'warning' as const, icon: <ApiIcon fontSize="small" /> };
      default: return { label: 'شحن بالمناطق', color: 'success' as const, icon: <MapIcon fontSize="small" /> };
    }
  };

  const typeInfo = getTypeLabel();

  return (
    <Card
      sx={{
        mb: 2,
        border: method.isActive ? '1px solid #e3f2fd' : '1px solid #eeeeee',
        opacity: method.isActive ? 1 : 0.7,
        transition: 'all 0.2s'
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" component="div">
                {method.name}
              </Typography>
              {method.isActive
                ? <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                : <CancelIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
              }
            </Box>
            <Typography variant="body2" color="text.secondary">
              {method.description}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip
              label={typeInfo.label}
              size="small"
              color={typeInfo.color}
              icon={typeInfo.icon}
            />
          </Box>
        </Box>

        {method.shippingType === 'pickup' && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            الاستلام من المتجر مجاني - لا توجد تكلفة شحن
          </Alert>
        )}

        {method.shippingType === 'api' && (
          <Box sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ApiIcon sx={{ color: 'warning.main', fontSize: 16 }} />
              <Typography variant="caption" fontWeight={600} color="warning.dark">
                التسعير تلقائي من {method.apiProvider || 'Bosta'} API
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              المدن والأسعار تُجلب مباشرة من الشركة حسب موقع العميل
            </Typography>
          </Box>
        )}

        {method.shippingType === 'fixed' && (
          <>
            {/* ملخص أسعار ومناطق */}
            {totalCities > 0 ? (
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<MapIcon />}
                    label={`${activeRegions} منطقة`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                  <Chip
                    icon={<LocationIcon />}
                    label={`${totalCities} مدينة`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                  {allCities.length > 0 && (
                    <Chip
                      icon={<PriceIcon />}
                      label={`${Math.min(...allCities.map(c => c.shippingCost))} - ${Math.max(...allCities.map(c => c.shippingCost))} ج.م`}
                      size="small"
                      variant="outlined"
                      color="success"
                    />
                  )}
                  <Chip
                    icon={<SpeedIcon />}
                    label={`${method.estimatedDeliveryDays} أيام`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {/* عرض المناطق */}
                {regions.filter(r => r.isActive).map(region => (
                  <RegionDisplayCard key={region.id} region={region} />
                ))}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                لم يتم تحديد مناطق وأسعار بعد - قم بالتعديل لإضافة المناطق
              </Alert>
            )}
          </>
        )}
      </CardContent>

      {editing && (
        <CardActions sx={{ pt: 0 }}>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            تعديل
          </Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
            حذف
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

// مكون تعديل شركة شحن
const ShippingMethodEditDialog: React.FC<{
  method: ShippingMethod;
  methodIndex: number;
  onUpdate: (index: number, method: ShippingMethod) => void;
  onClose: () => void;
  bostaCities: BostaCity[];
  onTestBosta: () => void;
  bostaApiKey: string;
  bostaTestResult: string | null;
}> = ({ method, methodIndex, onUpdate, onClose, bostaCities, onTestBosta, bostaApiKey, bostaTestResult }) => {
  const [localMethod, setLocalMethod] = useState<ShippingMethod>({ ...method });
  const [fetchingBosta, setFetchingBosta] = useState(false);
  const [bostaFetchMsg, setBostaFetchMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  const addRegion = () => {
    const newRegion: ShippingRegion = {
      id: `region-${Date.now()}`,
      name: 'منطقة جديدة',
      cities: [],
      isActive: true
    };
    setLocalMethod(prev => ({
      ...prev,
      regions: [...(prev.regions || []), newRegion]
    }));
  };

  const addDefaultRegions = () => {
    setLocalMethod(prev => ({
      ...prev,
      regions: JSON.parse(JSON.stringify(DEFAULT_EGYPT_SHIPPING_REGIONS))
    }));
  };

  // جلب المدن من Bosta وتحويلها لمناطق قابلة للتعديل
  const importFromBosta = async () => {
    setFetchingBosta(true);
    setBostaFetchMsg({ type: 'info', text: '⏳ جارٍ جلب المدن من Bosta...' });
    try {
      const cities = await getBostaCities();
      console.log('Bosta cities fetched:', cities.length, cities);

      if (cities.length === 0) {
        setBostaFetchMsg({
          type: 'error',
          text: '❌ لم يتم العثور على مدن. تأكد من إعداد مفتاح Bosta API الصحيح في قسم إعدادات Bosta أدناه، ثم اضغط "اختبار الاتصال" للتحقق.'
        });
        return;
      }

      const regions = convertBostaCitiesToShippingRegions(cities);
      const totalCities = regions.reduce((sum, r) => sum + r.cities.length, 0);

      console.log('Converted regions:', regions.length, 'cities:', totalCities);

      if (regions.length === 0 || totalCities === 0) {
        // حدث خطأ في التحويل - أضف المدن مباشرة كمنطقة واحدة
        const fallbackCities: ShippingCity[] = cities.map(c => ({
          id: `bosta-city-${c._id}`,
          name: c.name,
          shippingCost: 60,
          estimatedDeliveryDays: 3,
          isActive: true
        }));
        const fallbackRegion: ShippingRegion = {
          id: `bosta-region-all-${Date.now()}`,
          name: 'جميع المحافظات',
          cities: fallbackCities,
          isActive: true
        };
        setLocalMethod(prev => ({ ...prev, regions: [fallbackRegion] }));
        setBostaFetchMsg({
          type: 'success',
          text: `✅ تم جلب ${cities.length} مدينة من Bosta بنجاح في منطقة واحدة! يمكنك تعديل الأسعار وإعادة تنظيمها في مناطق.`
        });
        return;
      }

      setLocalMethod(prev => ({ ...prev, regions }));
      setBostaFetchMsg({
        type: 'success',
        text: `✅ تم جلب ${totalCities} مدينة في ${regions.length} منطقة من Bosta بنجاح! يمكنك الآن تعديل الأسعار.`
      });
    } catch (err: any) {
      console.error('Bosta import error:', err);
      setBostaFetchMsg({ type: 'error', text: '❌ فشل جلب البيانات: ' + (err?.message || 'خطأ غير معروف') });
    } finally {
      setFetchingBosta(false);
    }
  };

  const updateRegion = (regionIndex: number, updatedRegion: ShippingRegion) => {
    const updatedRegions = [...(localMethod.regions || [])];
    updatedRegions[regionIndex] = updatedRegion;
    setLocalMethod(prev => ({ ...prev, regions: updatedRegions }));
  };

  const deleteRegion = (regionIndex: number) => {
    const updatedRegions = (localMethod.regions || []).filter((_, i) => i !== regionIndex);
    setLocalMethod(prev => ({ ...prev, regions: updatedRegions }));
  };

  const handleSave = () => {
    onUpdate(methodIndex, localMethod);
    onClose();
  };

  return (
    <Paper sx={{ p: 3, mb: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          تعديل: {localMethod.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<SaveIcon />} onClick={handleSave}>
            حفظ
          </Button>
          <Button variant="outlined" size="small" onClick={onClose}>
            إلغاء
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="اسم شركة الشحن"
            value={localMethod.name}
            onChange={(e) => setLocalMethod(prev => ({ ...prev, name: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>نوع الشحن</InputLabel>
            <Select
              value={localMethod.shippingType || 'fixed'}
              label="نوع الشحن"
              onChange={(e) => setLocalMethod(prev => ({
                ...prev,
                shippingType: e.target.value as 'pickup' | 'fixed' | 'api',
                cost: e.target.value === 'pickup' ? 0 : prev.cost
              }))}
            >
              <MenuItem value="pickup">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon fontSize="small" />
                  استلام من المتجر (مجاني)
                </Box>
              </MenuItem>
              <MenuItem value="fixed">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon fontSize="small" />
                  تسعير حسب المنطقة
                </Box>
              </MenuItem>
              <MenuItem value="api">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ApiIcon fontSize="small" />
                  تسعير تلقائي من API
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="وصف شركة الشحن"
            value={localMethod.description}
            onChange={(e) => setLocalMethod(prev => ({ ...prev, description: e.target.value }))}
          />
        </Grid>

        {localMethod.shippingType !== 'pickup' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="التكلفة الافتراضية (إذا لم تحدد المنطقة)"
                type="number"
                value={localMethod.cost}
                onChange={(e) => setLocalMethod(prev => ({ ...prev, cost: Number(e.target.value) }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">ج.م</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="مدة التوصيل الافتراضية (أيام)"
                type="number"
                value={localMethod.estimatedDeliveryDays}
                onChange={(e) => setLocalMethod(prev => ({ ...prev, estimatedDeliveryDays: Number(e.target.value) }))}
              />
            </Grid>
          </>
        )}

        {localMethod.shippingType === 'api' && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>شركة الـ API</InputLabel>
              <Select
                value={localMethod.apiProvider || 'bosta'}
                label="شركة الـ API"
                onChange={(e) => setLocalMethod(prev => ({ ...prev, apiProvider: e.target.value }))}
              >
                <MenuItem value="bosta">Bosta بوسطه</MenuItem>
                <MenuItem value="other">شركة أخرى</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={localMethod.isActive}
                onChange={(e) => setLocalMethod(prev => ({ ...prev, isActive: e.target.checked }))}
              />
            }
            label="تفعيل شركة الشحن"
          />
        </Grid>
      </Grid>

      {/* مناطق شركة الشحن (للشركات العادية fixed) */}
      {localMethod.shippingType === 'fixed' && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={700}>
                المناطق والمدن وأسعار الشحن
              </Typography>
              {localMethod.regions && localMethod.regions.length > 0 && (
                <Chip
                  label={`${localMethod.regions.length} مناطق / ${localMethod.regions.reduce((s, r) => s + r.cities.length, 0)} مدينة`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* أزرار الإضافة */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={importFromBosta}
              disabled={fetchingBosta}
              startIcon={<BostaIcon />}
              sx={{ bgcolor: '#e53935', '&:hover': { bgcolor: '#c62828' } }}
            >
              {fetchingBosta ? 'جارٍ الجلب...' : '🚀 جلب مدن مصر من Bosta (مع التسعير)'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={addDefaultRegions}
              startIcon={<RefreshIcon />}
            >
              مناطق مصر الافتراضية
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={addRegion}
              startIcon={<AddIcon />}
            >
              إضافة منطقة يدوياً
            </Button>
          </Box>

          {/* نتيجة الجلب */}
          {bostaFetchMsg && (
            <Alert
              severity={bostaFetchMsg.type}
              sx={{ mb: 2 }}
              onClose={() => setBostaFetchMsg(null)}
            >
              {bostaFetchMsg.text}
            </Alert>
          )}

          {(!localMethod.regions || localMethod.regions.length === 0) ? (
            <Alert severity="info">
              <strong>لا توجد مناطق بعد.</strong><br />
              • اضغط <strong>"جلب مدن مصر من Bosta"</strong> لاستيراد جميع مدن مصر مع أسعار افتراضية قابلة للتعديل<br />
              • أو اضغط <strong>"مناطق مصر الافتراضية"</strong> لإضافة أسعار يدوية مبسطة
            </Alert>
          ) : (
            (localMethod.regions || []).map((region, regionIndex) => (
              <RegionEditCard
                key={region.id}
                region={region}
                regionIndex={regionIndex}
                onUpdate={updateRegion}
                onDelete={deleteRegion}
              />
            ))
          )}
        </Box>
      )}

      {/* معلومات Bosta API */}
      {localMethod.shippingType === 'api' && localMethod.apiProvider === 'bosta' && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Bosta API:</strong> المدن والأسعار ستُجلب تلقائياً من Bosta عند إتمام الطلب.
              تأكد من تفعيل وإعداد مفتاح Bosta API في قسم "إعدادات Bosta" أدناه.
            </Typography>
          </Alert>
          {bostaCities.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                المدن المتاحة من Bosta ({bostaCities.length} مدينة):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {bostaCities.slice(0, 10).map(city => (
                  <Chip key={city._id} label={city.name} size="small" variant="outlined" color="warning" />
                ))}
                {bostaCities.length > 10 && (
                  <Chip label={`+${bostaCities.length - 10} مزيد`} size="small" />
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

// ========================
// المكون الرئيسي
// ========================
const ShippingSettingsNew: React.FC<ShippingSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSettings, settings: storeSettings } = useStoreSettings();
  const [formData, setFormData] = useState<ShippingSettingsType>(settings);
  const [editing, setEditing] = useState(false);
  const [editingMethodIndex, setEditingMethodIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [bostaFormData, setBostaFormData] = useState<BostaSettings>(
    storeSettings?.bosta || {
      enabled: false, apiKey: '', defaultPickupCity: '', defaultPickupZone: '',
      defaultPickupDistrict: '', defaultPickupStreet: '', defaultPickupBuilding: '',
      defaultPickupPhone: '', deliveryType: '15', enableCOD: true
    }
  );
  const [bostaCities, setBostaCities] = useState<BostaCity[]>([]);
  const [bostaTestResult, setBostaTestResult] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'freeShippingThreshold') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? undefined : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onSave(true);
      onError(null);
      setIsLoading(true);
      setEditingMethodIndex(null);

      if (storeSettings) {
        const updatedSettings = {
          ...storeSettings,
          shipping: formData,
          bosta: bostaFormData,
          lastUpdated: new Date()
        };
        await updateSettings(updatedSettings);
      }

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      onError('حدث خطأ أثناء حفظ إعدادات الشحن');
    } finally {
      onSave(false);
      setIsLoading(false);
    }
  };

  const handleBostaChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setBostaFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setBostaFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleBostaSelectChange = (e: any) => {
    const { name, value } = e.target;
    setBostaFormData(prev => ({ ...prev, [name]: value }));
  };

  const testBostaConnection = async () => {
    try {
      setBostaTestResult(null);
      const response = await axios({
        method: 'GET',
        url: 'https://app.bosta.co/api/v2/cities?countryId=60e4482c7cb7d4bc4849c4d5',
        headers: {
          'Authorization': bostaFormData.apiKey,
          'Content-Type': 'application/json'
        }
      });
      const data = response.data;
      const list = data?.data?.list || data?.data || data?.cities || data?.result;
      let cities: any[] = [];
      if (Array.isArray(list)) cities = list;
      setBostaCities(cities);
      if (cities.length > 0) {
        setBostaTestResult(`✅ تم الاتصال بنجاح! تم العثور على ${cities.length} مدينة`);
      } else {
        setBostaTestResult('⚠️ تم الاتصال ولكن لم يتم العثور على مدن. تنسيق الاستجابة: ' + JSON.stringify(data).substring(0, 200));
      }
    } catch (error: any) {
      setBostaTestResult('❌ فشل الاتصال: ' + (error?.response?.data?.message || error?.message || 'خطأ غير معروف'));
    }
  };

  const addNewShippingMethod = () => {
    const newMethod: ShippingMethod = {
      id: `method-${Date.now()}`,
      name: 'شركة شحن جديدة',
      description: 'وصف شركة الشحن',
      cost: 50,
      estimatedDeliveryDays: 3,
      isActive: true,
      icon: 'local_shipping',
      shippingType: 'fixed',
      regions: JSON.parse(JSON.stringify(DEFAULT_EGYPT_SHIPPING_REGIONS))
    };
    const newIndex = formData.shippingMethods.length;
    setFormData(prev => ({
      ...prev,
      shippingMethods: [...prev.shippingMethods, newMethod]
    }));
    setEditingMethodIndex(newIndex);
  };

  const updateShippingMethod = (index: number, updatedMethod: ShippingMethod) => {
    const updatedMethods = [...formData.shippingMethods];
    updatedMethods[index] = updatedMethod;
    setFormData(prev => ({ ...prev, shippingMethods: updatedMethods }));
  };

  const deleteShippingMethod = (index: number) => {
    const updatedMethods = formData.shippingMethods.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, shippingMethods: updatedMethods }));
    if (editingMethodIndex === index) setEditingMethodIndex(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* رأس الصفحة */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات الشحن
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!editing ? (
            <Button startIcon={<EditIcon />} onClick={() => setEditing(true)} variant="outlined">
              تفعيل التعديل
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFormData(settings);
                  setBostaFormData(storeSettings?.bosta || bostaFormData);
                  setEditing(false);
                  setEditingMethodIndex(null);
                }}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                startIcon={<SaveIcon />}
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? 'جارٍ الحفظ...' : 'حفظ التغييرات'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* الإعدادات الأساسية */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          الإعدادات الأساسية
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enableShipping}
                  onChange={handleChange}
                  name="enableShipping"
                  disabled={!editing}
                  color="primary"
                />
              }
              label="تفعيل الشحن"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="الحد الأدنى للشحن المجاني"
              name="freeShippingThreshold"
              type="number"
              value={formData.freeShippingThreshold || ''}
              onChange={handleChange}
              disabled={!editing}
              helperText="اترك الحقل فارغًا إذا كنت لا ترغب في تفعيل الشحن المجاني"
              InputProps={{
                startAdornment: <InputAdornment position="start">ج.م</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="طريقة الشحن الافتراضية"
              name="defaultShippingMethod"
              select
              SelectProps={{ native: true }}
              value={formData.defaultShippingMethod}
              onChange={handleChange}
              disabled={!editing}
            >
              {formData.shippingMethods.map((method) => (
                <option key={method.id} value={method.id}>
                  {method.name}
                </option>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* شركات الشحن */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BostaIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>
              شركات الشحن
            </Typography>
            <Chip
              label={`${formData.shippingMethods.filter(m => m.isActive).length} مفعّل`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {editing && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={addNewShippingMethod}
            >
              إضافة شركة شحن
            </Button>
          )}
        </Box>

        {formData.shippingMethods.length === 0 ? (
          <Alert severity="info">
            لا توجد شركات شحن. قم بإضافة شركة شحن جديدة.
          </Alert>
        ) : (
          formData.shippingMethods.map((method, methodIndex) => (
            <Box key={method.id}>
              {/* وضع التعديل للشركة */}
              {editing && editingMethodIndex === methodIndex ? (
                <ShippingMethodEditDialog
                  method={method}
                  methodIndex={methodIndex}
                  onUpdate={updateShippingMethod}
                  onClose={() => setEditingMethodIndex(null)}
                  bostaCities={bostaCities}
                  onTestBosta={testBostaConnection}
                  bostaApiKey={bostaFormData.apiKey}
                  bostaTestResult={bostaTestResult}
                />
              ) : (
                <ShippingMethodCard
                  method={method}
                  editing={editing}
                  onEdit={() => setEditingMethodIndex(methodIndex)}
                  onDelete={() => deleteShippingMethod(methodIndex)}
                />
              )}
            </Box>
          ))
        )}
      </Paper>

      {/* أماكن الاستلام */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StoreIcon color="info" />
            <Typography variant="subtitle1" fontWeight={700}>
              أماكن الاستلام (مجاني)
            </Typography>
          </Box>
          {editing && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const newLocation: PickupLocation = {
                  id: `pickup-${Date.now()}`,
                  name: 'مكان استلام جديد',
                  address: '',
                  city: '',
                  phone: '',
                  workingHours: '',
                  isActive: true
                };
                setFormData(prev => ({
                  ...prev,
                  pickupLocations: [...(prev.pickupLocations || []), newLocation]
                }));
              }}
            >
              إضافة مكان استلام
            </Button>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enablePickup || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, enablePickup: e.target.checked }))}
                  disabled={!editing}
                />
              }
              label="تفعيل الاستلام من مكان محدد"
            />
          </Grid>
        </Grid>

        {formData.enablePickup && (
          formData.pickupLocations && formData.pickupLocations.length > 0 ? (
            formData.pickupLocations.map((location, locationIndex) => (
              <Card key={location.id} sx={{ mb: 2 }}>
                <CardContent>
                  {editing ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="اسم المكان"
                          value={location.name}
                          onChange={(e) => {
                            const updatedLocations = [...(formData.pickupLocations || [])];
                            updatedLocations[locationIndex] = { ...location, name: e.target.value };
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="المدينة"
                          value={location.city}
                          onChange={(e) => {
                            const updatedLocations = [...(formData.pickupLocations || [])];
                            updatedLocations[locationIndex] = { ...location, city: e.target.value };
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="العنوان"
                          value={location.address}
                          onChange={(e) => {
                            const updatedLocations = [...(formData.pickupLocations || [])];
                            updatedLocations[locationIndex] = { ...location, address: e.target.value };
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="رقم الهاتف"
                          value={location.phone || ''}
                          onChange={(e) => {
                            const updatedLocations = [...(formData.pickupLocations || [])];
                            updatedLocations[locationIndex] = { ...location, phone: e.target.value };
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="ساعات العمل"
                          value={location.workingHours || ''}
                          onChange={(e) => {
                            const updatedLocations = [...(formData.pickupLocations || [])];
                            updatedLocations[locationIndex] = { ...location, workingHours: e.target.value };
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={location.isActive}
                              onChange={(e) => {
                                const updatedLocations = [...(formData.pickupLocations || [])];
                                updatedLocations[locationIndex] = { ...location, isActive: e.target.checked };
                                setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                              }}
                              size="small"
                            />
                          }
                          label="مفعل"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => {
                            const updatedLocations = (formData.pickupLocations || []).filter((_, i) => i !== locationIndex);
                            setFormData(prev => ({ ...prev, pickupLocations: updatedLocations }));
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StoreIcon color="info" fontSize="small" />
                          <Typography variant="h6">{location.name}</Typography>
                        </Box>
                        {location.isActive
                          ? <Chip label="مفعل" size="small" color="success" />
                          : <Chip label="معطل" size="small" color="default" />
                        }
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <LocationIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {location.address}, {location.city}
                      </Typography>
                      {location.phone && (
                        <Typography variant="body2">📞 {location.phone}</Typography>
                      )}
                      {location.workingHours && (
                        <Typography variant="body2">🕐 {location.workingHours}</Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Alert severity="info">لا توجد أماكن استلام. قم بإضافة مكان استلام جديد.</Alert>
          )
        )}
      </Paper>

      {/* قسم Bosta للشحن */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BostaIcon sx={{ ml: 1, color: '#e53935' }} />
          <Typography variant="subtitle1" fontWeight={700}>إعدادات Bosta API</Typography>
        </Box>
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bosta هي خدمة شحن وتوصيل. قم بإدخال مفتاح API الخاص بك لتفعيل الشحن التلقائي عبر Bosta.
            عند إضافة شركة شحن من نوع "API - Bosta"، ستُجلب أسعار ومدن التوصيل تلقائياً.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={bostaFormData.enabled} onChange={(e) => setBostaFormData(prev => ({ ...prev, enabled: e.target.checked }))} disabled={!editing} />}
                label="تفعيل الشحن عبر Bosta"
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField fullWidth label="مفتاح API" name="apiKey" type="password" dir="ltr"
                value={bostaFormData.apiKey} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button variant="outlined" onClick={testBostaConnection} disabled={!bostaFormData.apiKey || !editing} fullWidth>
                اختبار الاتصال وجلب المدن
              </Button>
            </Grid>
            {bostaTestResult && (
              <Grid item xs={12}>
                <Alert severity={bostaTestResult.includes('✅') ? 'success' : bostaTestResult.includes('⚠️') ? 'warning' : 'error'}>
                  {bostaTestResult}
                </Alert>
              </Grid>
            )}

            {bostaCities.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  المدن المتاحة من Bosta:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {bostaCities.map(city => (
                    <Chip key={city._id} label={city.name} size="small" variant="outlined" color="error" />
                  ))}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}><Divider /><Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>عنوان الاستلام الافتراضي</Typography></Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!editing}>
                <InputLabel>المدينة</InputLabel>
                <Select name="defaultPickupCity" value={bostaFormData.defaultPickupCity} onChange={handleBostaSelectChange} label="المدينة">
                  {bostaCities.map(city => (
                    <MenuItem key={city._id} value={city._id}>{city.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="المنطقة (Zone)" name="defaultPickupZone" value={bostaFormData.defaultPickupZone} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الحي (District)" name="defaultPickupDistrict" value={bostaFormData.defaultPickupDistrict} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الشارع" name="defaultPickupStreet" value={bostaFormData.defaultPickupStreet} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="رقم المبنى" name="defaultPickupBuilding" value={bostaFormData.defaultPickupBuilding} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="الدور" name="defaultPickupFloor" value={bostaFormData.defaultPickupFloor || ''} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="الشقة" name="defaultPickupApartment" value={bostaFormData.defaultPickupApartment || ''} onChange={handleBostaChange} disabled={!editing} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="رقم هاتف الاستلام" name="defaultPickupPhone" dir="ltr" value={bostaFormData.defaultPickupPhone} onChange={handleBostaChange} disabled={!editing} />
            </Grid>

            <Grid item xs={12}><Divider /><Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>إعدادات التوصيل</Typography></Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!editing}>
                <InputLabel>نوع التوصيل</InputLabel>
                <Select name="deliveryType" value={bostaFormData.deliveryType} onChange={handleBostaSelectChange} label="نوع التوصيل">
                  <MenuItem value="10">توصيل فقط (Deliver)</MenuItem>
                  <MenuItem value="15">تحصيل نقدي (Cash Collection)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={bostaFormData.enableCOD} onChange={(e) => setBostaFormData(prev => ({ ...prev, enableCOD: e.target.checked }))} disabled={!editing} />}
                label="تفعيل الدفع عند الاستلام (COD)" />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setBostaFormData(storeSettings?.bosta || bostaFormData);
              setEditing(false);
              setEditingMethodIndex(null);
            }}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'جارٍ الحفظ...' : 'حفظ جميع التغييرات'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ShippingSettingsNew;
